import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import _ from "lodash";
import { requestDownloadDocumentById } from "store/project/reducer";
import { setAlertMessageRequest } from "store/ui/reducer";
import { allUsersSelector } from "store/user/selector";

export function useProjectDetailTable(tableData: any, onUpdateDocument: Function) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sorting, setSorting] = useState<SortingState>([]);

  const allUsers = useSelector(allUsersSelector);
  
  const translators = useMemo(() => {
    if (allUsers) {
      return allUsers?.translators?.filter((it: any) => it.deleted_at == null) || [];
    }
    return [];
  }, [allUsers]);

  const managers = useMemo(() => {
    if (allUsers) {
      return allUsers?.projectManagers?.filter((it: any) => it.deleted_at == null) || [];
    }
    return [];
  }, [allUsers]);

  const defaultData = useMemo(() => 
    tableData.map((data: any) => ({
      ...data,
      translatorHasChanged: false,
    })), [tableData]
  );

  const [data, setData] = useState(() => [...defaultData]);

  useEffect(() => {
    setData([...defaultData]);
  }, [defaultData]);

  const onChangeDocumentStatus = (document_id: string, option: any) => {
    const index = data?.findIndex((it) => it.document_id === document_id);
    if (index > -1) {
      const dumpData = _.cloneDeep(data);
      dumpData[index].document_status_id = option.id;
      dumpData[index].hasChanged = true;
      setData([...dumpData]);
    }
  };

  const onAssignTranslator = (document_id: string, translator: any) => {
    const index = data?.findIndex((it) => it.document_id === document_id);
    if (index > -1) {
      const dumpData = _.cloneDeep(data);
      if (dumpData[index].translators.map((item: any) => item.user_id).includes(translator.user_id)) {
        dumpData[index].translators = dumpData[index].translators.filter(
          (id: any) => id.user_id !== translator.user_id
        );
      } else {
        dumpData[index].translators = [...dumpData[index].translators, translator];
      }
      dumpData[index].translatorHasChanged = true;
      dumpData[index].hasChanged = true;
      setData([...dumpData]);
    }
  };

  const onAssignManager = (document_id: string, manager: any) => {
    const index = data?.findIndex((it) => it.document_id === document_id);
    if (index > -1) {
      const dumpData = _.cloneDeep(data);
      dumpData[index].managers = [manager];
      dumpData[index].hasChanged = true;
      setData([...dumpData]);
    }
  };

  const onClickSaveDocument = (document_id: string) => {
    const index = data?.findIndex((it) => it.document_id === document_id);
    if (index === -1) return;

    // Guard: cannot mark Completed without a translated file present.
    // Either a freshly attached new_files upload or an existing lastest_file is required.
    const selectedStatus = Number(data[index].document_status_id);
    const hasNewFileUpload = Boolean(data[index].new_files && data[index].new_files.length > 0);
    const hasExistingTranslatedFile = Boolean(data[index].lastest_file?.document_file_path);
    if (selectedStatus === 4 && !hasNewFileUpload && !hasExistingTranslatedFile) {
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message: "Cannot mark Completed without a translated file. Upload a file or select a different status.",
        })
      );
      return;
    }

    if (index > -1) {
      // When admin saves with a new translated file attached, mark as Completed
      // (4 per backend, see app/Controllers/_Projects.php#811). Previously this
      // sent 5 which backend treats as "In Revision" — that's why customers
      // never saw completed projects.
      const sendData = data[index].new_files
        ? { ...data[index], document_status_id: 4 }
        : data[index];
      
      if (data[index].translatorHasChanged === false) {
        const { translators, ...dataWithoutTranslators } = sendData;
        onUpdateDocument(dataWithoutTranslators);
      } else {
        onUpdateDocument(sendData);
      }
    }
  };

  const onDownloadFiles = (document_id: any, file_name: any) => {
    dispatch(
      requestDownloadDocumentById({
        document_id: document_id,
        file_name: `${file_name}-files.zip`,
      })
    );
  };

  const handleChange = (document_id: any, files: any) => {
    const index = data?.findIndex((it) => it.document_id === document_id);
    if (index > -1) {
      const dumpData = _.cloneDeep(data);
      dumpData[index].new_files = Array.from(files);
      dumpData[index].hasChanged = true;
      setData([...dumpData]);
    }
  };

  const onClickView = (it: any) => {
    if (Number(it?.user_role_type_id) === 3) {
      navigate("/admin/team#manager");
    } else if (Number(it?.user_role_type_id) === 6) {
      navigate("/admin/team#translator");
    }
  };

  const onTargetLanguageChange = (documentId: string, nextValue: string) => {
    const index = data?.findIndex((it: any) => it.document_id === documentId);
    if (index > -1) {
      const dumpData: any = _.cloneDeep(data);
      dumpData[index].target_lang = nextValue;
      dumpData[index].hasChanged = true;
      setData([...dumpData]);
    }
  };

  return {
    data,
    sorting,
    setSorting,
    translators,
    managers,
    onChangeDocumentStatus,
    onAssignTranslator,
    onAssignManager,
    onClickSaveDocument,
    onDownloadFiles,
    handleChange,
    onClickView,
    onTargetLanguageChange,
  };
}
